import { useEffect, useMemo, useState } from "react";

import { projects } from "@/data";
import type { Project } from "@/data";

import ProjectGrid from "@/components/projects/ProjectGrid";
import ProjectModal from "@/components/projects/ProjectModal";

type Props = {
  mode?: "default" | "portfolio";
};

export default function ProjectsPage({ mode = "default" }: Props) {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  useEffect(() => {
    if (mode === "portfolio") {
      document.title = "Portfolio";
    } else {
      document.title = "프로젝트 포트폴리오 | 4-lab";
    }
  }, [mode]);

  // 모달 UX
  useEffect(() => {
    if (!selectedProject) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSelectedProject(null);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [selectedProject]);

  const categories = [
    { id: "all", name: "전체" },
    { id: "e-document", name: "E-DOCUMENT" },
    { id: "government", name: "GOVERNMENT" },
    { id: "mydata", name: "MYDATA" },
    { id: "ai", name: "A.I" },
    { id: "web-mobile", name: "WEB & MOBILE" },
  ];

  const getStartYear = (year?: string) => {
    if (!year) return 0;
    const match = year.match(/\d{4}/);
    return match ? parseInt(match[0], 10) : 0;
  };

  const isOngoingProject = (year?: string) => {
    if (!year) return false;

    if (year.includes("현재")) return true;
    if (!year.includes("~")) return false;

    const years = year.match(/\d{4}/g) ?? [];
    if (years.length === 1) return true;

    const endYear = parseInt(years[1], 10);
    const currentYear = new Date().getFullYear();

    return endYear >= currentYear;
  };

  const filteredProjects = useMemo(() => {
    const list =
      selectedCategory === "all"
        ? projects
        : projects.filter((p) => p.category === selectedCategory);

    return [...list].sort((a, b) => {
      const aOngoing = isOngoingProject(a.year);
      const bOngoing = isOngoingProject(b.year);

      if (aOngoing !== bOngoing) return aOngoing ? -1 : 1;

      return getStartYear(b.year) - getStartYear(a.year);
    });
  }, [selectedCategory, projects]);

  return (
    <>
      <div className={mode === "portfolio" ? "" : "pt-20"}>
        {/* Hero */}
        <section className="py-36 bg-gradient-to-b from-[#0A1F1F] to-[#0D9488]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-4xl">
              <span className="text-sm font-medium text-[#5EEAD4] mb-4 block">
                프로젝트
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-8">
                프로젝트 포트폴리오
              </h1>
              <p className="text-lg text-gray-200 leading-relaxed">
                공공·기업 환경에서 수행한 프로젝트 경험을 바탕으로,
                <br />
                AI와 데이터 기술을 적용한 구현 사례를 소개합니다.
              </p>
            </div>
          </div>
        </section>

        {/* Projects */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-wrap gap-3 mb-12">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.id
                      ? "bg-[#0D9488] text-white"
                      : "bg-[#F0FDFA] text-[#0F766E] hover:bg-[#CCFBF1]"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            <ProjectGrid
              projects={filteredProjects}
              onSelect={(project) => setSelectedProject(project)}
            />
          </div>
        </section>
      </div>

      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </>
  );
}
